/**
 * PortalContext.js — firma portalının kabuğu (backlog #311, 2026-10-11).
 *
 * Firma tarafı her giriş (personel olmayan: #307) için seçili fuarın portal bağlamını bir kez çeker
 * (GET /portal/context) ve menüye, rotalara, başlıktaki rol değiştiriciye ve sayfalara dağıtır:
 *   - firma: { firmaId, firmaName, yabanci } — sayfalar firma id'sini BURADAN alır; çözülemezse hata
 *     gösterilir, hiçbir zaman test dekoratörüne (49858) düşülmez.
 *   - arayuzler: firmanın bu fuardaki her rolü × uyruğu (Yerli/Yabancı × Katılımcı/Dekoratör/Organizatör)
 *   - aktif: kullanıcının seçtiği set — fuar başına hatırlanır (localStorage `portalArayuz:<fuarId>`).
 *     Aynı fuarda kendi dekoratörü olan bir katılımcı iki set görür ve başlıktan geçer.
 *   - seviye(modul): aktif setin o modüldeki seviyesi (NONE/READ/WRITE/MANAGE) — menü ve düğmeler bununla.
 *   - eylem(kod): organizatör eyleminin durumu {izinli, rolIzni, firmaAyari, sebep}.
 *   - alan(kod): #446 — organizatörün bir ALANDAKİ kademesi {alan, kademe: NONE|MONITOR|AUDIT, gorebilir, denetleyebilir, gerekce}. Alan yoksa /
 *     organizatör değilse hepsi kapalı. Bir ÖNİZLEME hiçbir şeyi denetlemez (denetleyebilir hep false).
 *
 * #418 (2026-10-14): girişin portal rolleri ve "neden yapamıyorum?" gerekçeleri:
 *   - kullaniciRolleri: [{kod, ad, kaynak}] — bu girişin bu firmadaki rolleri (ad oturum dilinde; backend Accept-Language'e bakar,
 *     dil değişince bağlam yeniden çekilir). rolKaynagi: ATANMIS / VARSAYILAN / SONLANDIRILMIS / YOK / ONIZLEME.
 *   - ipucu(modul, min): seviye min'e ulaşmıyorsa nedenini söyleyen cümle (SET_CAP / ROLE / COMPANY_RESTRICTION), yoksa null.
 *   - eylemIpucu(kod, set): bir organizatör eylemi kapalıysa nedeni (… / ORGANIZER_SETTING), açıksa null.
 *   Bunlar yalnızca GÖRÜNÜM: backend aynı kuralı uçlarda uygular, reddedilen istek de nedeni söyler.
 *
 * Personel girişinde hiçbir şey çekilmez (portal: null); personel sayfaları bu bağlamı okumaz.
 *
 * #411 (2026-10-13): TEK İSTİSNA — "Portalı bu firma olarak önizle". page.firma yetkisi olan bir personel, Firma Detay'dan
 * bir firmanın portalını seçili fuarda salt okunur açabilir (services/portalOnizleme.js). O zaman bağlam, backend'in
 * personel önizlemesinden (GET /portal/context?firmaId=, onizleme: true) gelir ve değer `onizleme: true` taşır; seviye()
 * her modülü en çok READ'e indirir, eylem() her organizatör eylemini kapatır; yazma isteklerini ayrıca axios
 * interceptor'ı keser. Personel gerçek bir firma girişi gibi davranmaz: kendi oturumu değişmez.
 *
 * #704 (2026-11-03, FDS-Backend #703): `sadePanel` = Sade panel modu AÇIK (bağlamın `sadePanel`ı). Düzen services/sadePanel.js'in
 * sadePanelMi'sine bakar, set koduna değil. Anahtarı personel istediği an çevirir, bu yüzden bir firma girişi bağlamı ARKADA da yeniden
 * okur: bir modülü adlandıran 403'te (en çok 15 sn'de bir) ve 5 dakikada bir (sekme görünürken). Bu okuma `yukleniyor`a dokunmaz,
 * hata verirse eldekini bırakır: sayfa yanıp sönmez, tur kapanmaz.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import MainContext from './MainContext';
// WAS (#535, until #562): import { LanguageContext } from './LanguageContext';
// WAS (#535, until #562): import { dilSecildiMi, yabanciIcinVarsayilanDil } from '../../services/dilVarsayilani';
// WAS (until #411): import { isFirmaAccount } from '../../services/firmaSessionService';
import { isFirmaAccount, isStaffAccount } from '../../services/firmaSessionService';
import { getPortalContext, enAz, SEVIYE } from '../../services/portalService';
import { modulIpucu, eylemIpucu as eylemIpucuMetni } from '../../services/portalGerekce';
import { useOnizleme } from '../../services/portalOnizleme';
import { modulReddiMi, BAGLAM_YOKLAMA_MS, RED_YENILEME_ARALIGI_MS } from '../../services/sadePanel';
import i18n from "../../i18n";
import { useTranslation } from 'react-i18next';

const PortalContext = createContext(null);

const anahtar = (fuarId) => `portalArayuz:${fuarId}`;

function kayitliArayuz(fuarId) {
  try {
    return fuarId ? localStorage.getItem(anahtar(fuarId)) : null;
  } catch {
    return null;
  }
}

export const PortalProvider = ({ children }) => {
  const { selectedFuarId } = useContext(MainContext);
  // #403: a logout -> login as another account is a redirect and changes nothing in MainContext on the same fair, so
  // this provider would not re-render and `girisToken` below would stay the old session's. Listening to the location
  // makes it re-read the token on every navigation (the mechanism of the first #403 implementation, kept here).
  useLocation();
  // #418: re-render (and refetch, see the effect) when the UI language changes: role names and reasons come in the request's language.
  const { i18n: dil } = useTranslation();
  const [veri, setVeri] = useState(null);
  // WAS (#535, until #562): const [veriTokeni, setVeriTokeni] = useState(null); -- the session token `veri` was fetched for, read only by the
  // foreign-login language default below, which the server's per-user language replaced.
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [secili, setSecili] = useState(null);
  const [tetik, setTetik] = useState(0);
  // #411: { firmaId, firmaAdi, arayuz } while a staff login previews a company's portal, else null.
  const onizleme = useOnizleme();

  // #403: the session token is a dependency of the fetch below, so logging out and in as ANOTHER company on the
  // same fair (same selectedFuarId, no reload) refetches /portal/context instead of keeping the first company's
  // firmaId, which the Firma pages now read from here.
  const girisToken = localStorage.getItem('token');
  // WAS (until #403): const firmaGirisi = Boolean(localStorage.getItem('token')) && isFirmaAccount();
  const firmaGirisi = Boolean(girisToken) && isFirmaAccount();
  // #411: a staff login previewing ONE company at the selected fair, read-only (the backend answers only staff with page.firma).
  const personelOnizleme = Boolean(girisToken) && isStaffAccount() && onizleme != null;
  // The preview's fields as plain values: the callbacks below name THESE (the React Compiler's lint wants the inferred
  // dependencies to match the listed ones, and a nested optional path does not).
  const ofid = onizleme?.firmaId ?? null;
  const okid = onizleme?.kullaniciId ?? null;
  const ofad = onizleme?.firmaAdi ?? null;
  const okad = onizleme?.kullaniciAdi ?? null;
  const oarayuz = onizleme?.arayuz ?? null;
  const portalAcik = firmaGirisi || personelOnizleme;
  // #704: every read takes a number; a background read that a newer read overtook (another fair, another login) is thrown away.
  const sonIstek = useRef(0);
  // The remembered set is kept per fair for a company login and per (company, fair) in a preview, so a preview never
  // changes what a real company login of the same browser sees.
  // WAS (until #417): const arayuzAnahtari = personelOnizleme ? `onizleme:${onizleme.firmaId}:${selectedFuarId}` : selectedFuarId;
  const arayuzAnahtari = personelOnizleme ? `onizleme:${onizleme.firmaId}:${onizleme.kullaniciId ?? 0}:${selectedFuarId}` : selectedFuarId;

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      sonIstek.current += 1;   // #704: a background read still in flight no longer applies
      setYukleniyor(true);
      setHata(null);
      try {
        // WAS (until #411): const out = await getPortalContext({ fuarId: selectedFuarId, arayuz: kayitliArayuz(selectedFuarId) });
        const out = await getPortalContext({
          fuarId: selectedFuarId,
          arayuz: kayitliArayuz(arayuzAnahtari) ?? (personelOnizleme ? onizleme.arayuz : null),
          firmaId: personelOnizleme ? onizleme.firmaId : undefined,
          kullaniciId: personelOnizleme ? onizleme.kullaniciId ?? undefined : undefined,
        });
        if (!ignore) {
          setVeri(out);
          // WAS (#535, until #562): setVeriTokeni(girisToken);
          setSecili(out?.aktifArayuz ?? null);
        }
      } catch (e) {
        if (!ignore) {
          setVeri(null);
          setHata(e.message || i18n.t("components:portalContext.firmaPortaliBilgisiAlinamadi"));
        }
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    // WAS (until #411): if (firmaGirisi) run();
    if (portalAcik) run();
    return () => { ignore = true; };
    // WAS (until #403): }, [firmaGirisi, selectedFuarId, tetik]);
    // WAS (until #411): }, [firmaGirisi, girisToken, selectedFuarId, tetik]);
    // WAS (until #417): }, [portalAcik, personelOnizleme, onizleme?.firmaId, onizleme?.arayuz, girisToken, selectedFuarId, tetik]);
    // #418: the language too: role names and reasons come in the request's language, so a TR / EN switch refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalAcik, personelOnizleme, onizleme?.firmaId, onizleme?.kullaniciId, onizleme?.arayuz, girisToken, selectedFuarId, tetik, dil.language]);

  // #562 (2026-10-30): the foreign-login English default (#535) moved to the server. GET /users/me/language answers English for a foreign
  // user or a user of a foreign company unless the user chose, and it is applied at login (pages/Home/Login) and at app start
  // (LanguageContext), so this provider no longer switches the language; a staff preview never did and still cannot.
  // WAS (#535, until #562):
  // #535 (2026-10-21, Deniz): a foreign company login starts in English, unless that login chose a language itself (services/dilVarsayilani.js).
  // The switch changes dil.language, which refetches the context above in English (role names, reasons) — one extra read, once.
  // undefined outside LanguageProvider (tests): nothing to switch then.
  // const setSelectedLang = useContext(LanguageContext)?.setSelectedLang;
  // const yabanciFirma = portalAcik && veriTokeni === girisToken && Boolean(veri?.firma?.yabanci);
  // useEffect(() => {
  //   const hedef = yabanciIcinVarsayilanDil({ yabanci: yabanciFirma, onizleme: personelOnizleme, secildi: dilSecildiMi(), mevcut: dil.language });
  //   if (hedef && setSelectedLang) setSelectedLang(hedef);
  // }, [yabanciFirma, personelOnizleme, dil.language, setSelectedLang]);

  const arayuzler = useMemo(() => veri?.arayuzler ?? [], [veri]);
  const aktif = useMemo(
    () => arayuzler.find((a) => a.kod === secili) ?? arayuzler[0] ?? null,
    [arayuzler, secili],
  );

  const setArayuz = useCallback((kod) => {
    setSecili(kod);
    try {
      // WAS (until #411): if (veri?.fuarId) localStorage.setItem(anahtar(veri.fuarId), kod);
      // WAS (until #417): anahtar(personelOnizleme ? `onizleme:${onizleme.firmaId}:${veri.fuarId}` : veri.fuarId)
      if (veri?.fuarId) {
        localStorage.setItem(anahtar(personelOnizleme ? `onizleme:${ofid}:${okid ?? 0}:${veri.fuarId}` : veri.fuarId), kod);
      }
    } catch {
      /* hatırlanamazsa yalnızca bu oturumda geçerli */
    }
    // WAS (until #411): }, [veri]);
    // WAS (until #417): }, [veri, personelOnizleme, onizleme?.firmaId]);
  }, [veri, personelOnizleme, ofid, okid]);

  // WAS (until #411): const seviye = useCallback((modul) => aktif?.moduller?.[modul] ?? SEVIYE.NONE, [aktif]);
  // #411: an ÖNİZLEME never writes: a level above READ is shown as READ, so every write button is hidden / disabled.
  const seviye = useCallback((modul) => {
    const s = aktif?.moduller?.[modul] ?? SEVIYE.NONE;
    return personelOnizleme && (s === SEVIYE.WRITE || s === SEVIYE.MANAGE) ? SEVIYE.READ : s;
  }, [aktif, personelOnizleme]);
  const enAzModul = useCallback((modul, min) => enAz(seviye(modul), min), [seviye]);
  // WAS (until #411): const eylem = useCallback((kod) => (aktif?.eylemler ?? []).find((e) => e.kod === kod) ?? { kod, izinli: false }, [aktif]);
  const eylem = useCallback(
    (kod) => {
      const e = (aktif?.eylemler ?? []).find((x) => x.kod === kod) ?? { kod, izinli: false };
      return personelOnizleme ? { ...e, izinli: false, sebep: 'ONIZLEME' } : e;
    },
    [aktif, personelOnizleme],
  );
  // #446: the tier of one area for the organizer set of this fair; closed (NONE) for anything else.
  const alan = useCallback((kod) => {
    const a = (aktif?.alanlar ?? []).find((x) => x.alan === kod);
    if (!a) return { alan: kod, kademe: 'NONE', gorebilir: false, denetleyebilir: false, gerekce: null };
    return personelOnizleme ? { ...a, denetleyebilir: false } : a;
  }, [aktif, personelOnizleme]);
  const yenile = useCallback(() => setTetik((t) => t + 1), []);

  // #704: the background read: the same request as the effect above, but `yukleniyor` / `hata` are never touched and a failure keeps
  // what is on screen. Only a newer read than every other one is applied.
  const sessizYenile = useCallback(async () => {
    if (!portalAcik) return;
    sonIstek.current += 1;
    const no = sonIstek.current;
    try {
      const out = await getPortalContext({
        fuarId: selectedFuarId,
        arayuz: kayitliArayuz(arayuzAnahtari) ?? (personelOnizleme ? oarayuz : null),
        firmaId: personelOnizleme ? ofid : undefined,
        kullaniciId: personelOnizleme ? okid ?? undefined : undefined,
      });
      if (no !== sonIstek.current) return;
      setVeri(out);
      setSecili(out?.aktifArayuz ?? null);
    } catch {
      /* the next read (or a reload) tries again; the page keeps the context it has */
    }
  }, [portalAcik, selectedFuarId, arayuzAnahtari, personelOnizleme, oarayuz, ofid, okid]);
  const sessizYenileRef = useRef(sessizYenile);
  useEffect(() => { sessizYenileRef.current = sessizYenile; }, [sessizYenile]);

  // #704: Sade panel modu reaches an open company portal without a reload: re-read on a refusal that names a module (the switch went ON,
  // or anything else that closed a module) and every few minutes while the tab is visible (the switch went OFF: nothing is refused then).
  // A staff preview is left alone (staff reload Firma Detay's preview themselves).
  const sonRedYenileme = useRef(0);
  useEffect(() => {
    if (!firmaGirisi) return undefined;
    const dinleyici = axios.interceptors.response.use((cevap) => cevap, (error) => {
      if (modulReddiMi(error) && Date.now() - sonRedYenileme.current > RED_YENILEME_ARALIGI_MS) {
        sonRedYenileme.current = Date.now();
        sessizYenileRef.current();
      }
      return Promise.reject(error);
    });
    const yoklama = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      sessizYenileRef.current();
    }, BAGLAM_YOKLAMA_MS);
    return () => {
      axios.interceptors.response.eject(dinleyici);
      clearInterval(yoklama);
    };
  }, [firmaGirisi]);

  // #418: the login's roles in this company and the "why can't I?" sentences. In a staff PREVIEW nothing writes, so a
  // level the set allows still says why it is closed (the preview) instead of staying silent.
  const kullaniciRolleri = useMemo(() => veri?.kullaniciRolleri ?? [], [veri]);
  const ipucu = useCallback((modul, min = SEVIYE.WRITE) => {
    const metin = modulIpucu(aktif, modul, min, kullaniciRolleri);
    if (metin) return metin;
    return personelOnizleme && !enAz(SEVIYE.READ, min) ? i18n.t('portal:gerekce.ONIZLEME') : null;
  }, [aktif, kullaniciRolleri, personelOnizleme]);
  const eylemIpucu = useCallback((kod, set) => {
    const metin = eylemIpucuMetni(set ?? aktif, kod, kullaniciRolleri);
    if (metin) return metin;
    return personelOnizleme ? i18n.t('portal:gerekce.ONIZLEME') : null;
  }, [aktif, kullaniciRolleri, personelOnizleme]);

  const deger = useMemo(() => {
    // WAS (until #411): if (!firmaGirisi) return null;
    if (!portalAcik) return null;
    return {
      // #411: true while a staff login previews this company (read-only); the banner and the menu read it.
      onizleme: personelOnizleme,
      onizlemeFirmaAdi: personelOnizleme ? ofad : null,
      onizlemeKullaniciAdi: personelOnizleme ? okad || null : null,   // #417
      yukleniyor,
      hata,
      hazir: !yukleniyor && !hata && veri != null,
      veri,
      firma: veri?.firma ?? null,
      firmaId: veri?.firma?.firmaId ?? null,
      yabanci: Boolean(veri?.firma?.yabanci),
      fuarId: veri?.fuarId ?? selectedFuarId ?? null,
      uyari: veri?.uyari ?? null,
      // #704 (FDS-Backend #703): Sade panel modu is ON; the layout asks services/sadePanel.sadePanelMi, never this alone.
      sadePanel: veri?.sadePanel === true,
      // WAS (until #419): izinler: veri?.izinler ?? [],  -- the legacy list of portal permission keys; the permissions 7-15 are retired
      // #418
      kullaniciRolleri,
      rolKaynagi: veri?.rolKaynagi ?? null,
      ipucu,
      eylemIpucu,
      arayuzler,
      aktif,
      setArayuz,
      seviye,
      enAzModul,
      eylem,
      alan,
      yenile,
    };
    // WAS (until #411): }, [firmaGirisi, yukleniyor, hata, veri, selectedFuarId, arayuzler, aktif, setArayuz, seviye, enAzModul, eylem, yenile]);
    // WAS (until #418): }, [portalAcik, personelOnizleme, onizleme?.firmaAdi, yukleniyor, hata, veri, selectedFuarId, arayuzler, aktif, setArayuz, seviye, enAzModul, eylem, yenile]);
    // WAS (until #446): ... seviye, enAzModul, eylem, yenile, kullaniciRolleri, ipucu, eylemIpucu]);
  }, [portalAcik, personelOnizleme, ofad, okad, yukleniyor, hata, veri, selectedFuarId, arayuzler, aktif, setArayuz, seviye, enAzModul, eylem, alan, yenile,
    kullaniciRolleri, ipucu, eylemIpucu]);

  return <PortalContext.Provider value={deger}>{children}</PortalContext.Provider>;
};

/** Firma girişinde portal bağlamı; personel girişinde null. */
export const usePortal = () => useContext(PortalContext);

export default PortalContext;
